import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import type { MonitoramentoItem, UsuarioWorkflow } from './models';
import workflowService from './workflowService';
import {
  AcaoContaVencida, contasVencidas, montarPedidoContaVencida, podeGerirContasVencidas, rotuloAcaoContaVencida,
} from './contasVencidas';
import { extrairMensagemErro } from './mensagemErro';
import { textoHoras } from './alertaMonitoramento';

const money = (v: number) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v);

const ACOES: AcaoContaVencida[] = ['APROVAR', 'PRORROGAR_PRAZO', 'BLOQUEAR', 'DEVOLVER_AO_AUTOR'];

/**
 * Governança de TI → contas vencidas (prazo de SLA expirado). Administrador de TI e níveis superiores (N5+) tratam a
 * conta com justificativa SOX: aprovar, prorrogar o prazo, bloquear ou devolver ao autor. As demais pessoas só
 * consultam. O backend valida permissão, alçada e segregação de funções, e o motivo de qualquer bloqueio volta aqui.
 */
const GovernancaContasVencidas: React.FC = () => {
  const history = useHistory();
  const [perfil, setPerfil] = useState<UsuarioWorkflow | null>(null);
  const [itens, setItens] = useState<MonitoramentoItem[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [aberta, setAberta] = useState<{ id: number; acao: AcaoContaVencida } | null>(null);
  const [justificativa, setJustificativa] = useState('');
  const [novoPrazo, setNovoPrazo] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [mensagem, setMensagem] = useState<{ tipo: 'ok' | 'erro'; texto: string } | null>(null);

  const carregar = useCallback(() => {
    setCarregando(true);
    workflowService.listarMonitoramento()
      .then(m => setItens(contasVencidas(m?.itens ?? [])))
      .catch(err => { setItens([]); setMensagem({ tipo: 'erro', texto: extrairMensagemErro(err, 'Não foi possível carregar as contas vencidas.') }); })
      .finally(() => setCarregando(false));
  }, []);

  useEffect(() => {
    workflowService.getMeuPerfil().then(setPerfil).catch(() => setPerfil(null));
    carregar();
  }, [carregar]);

  const podeGerir = podeGerirContasVencidas(perfil);

  const abrir = (id: number, acao: AcaoContaVencida) => {
    setAberta(aberta?.id === id && aberta.acao === acao ? null : { id, acao });
    setJustificativa('');
    setNovoPrazo('');
    setMensagem(null);
  };

  const confirmar = async (item: MonitoramentoItem) => {
    if (!aberta) return;
    const acao = aberta.acao;
    setMensagem(null);
    setEnviando(true);
    try {
      if (acao === 'APROVAR') {
        if (!justificativa.trim()) { setMensagem({ tipo: 'erro', texto: 'Informe a justificativa SOX.' }); return; }
        await workflowService.aprovarConciliacao(item.conciliacaoId, justificativa.trim());
      } else {
        const pedido = montarPedidoContaVencida(acao, justificativa, novoPrazo);
        if ('erro' in pedido) { setMensagem({ tipo: 'erro', texto: pedido.erro }); return; }
        await workflowService.alterarStatusContaVencida(item.conciliacaoId, pedido.corpo);
      }
      setAberta(null);
      setJustificativa('');
      setNovoPrazo('');
      setMensagem({ tipo: 'ok', texto: `Conciliação #${item.conciliacaoId}: ${rotuloAcaoContaVencida[acao].toLowerCase()} concluído. Registro SOX gerado.` });
      carregar();
    } catch (err) {
      setMensagem({ tipo: 'erro', texto: extrairMensagemErro(err, 'Não foi possível concluir a ação na conta vencida.') });
    } finally {
      setEnviando(false);
    }
  };

  return (
    <section className='wf-card' style={{ marginTop: 20 }}>
      <div className='wf-card__header'>
        <div><h2>Contas vencidas</h2><p>Conciliações com prazo de SLA expirado que aguardam tratamento da governança.</p></div>
      </div>
      {mensagem && (
        <div className={mensagem.tipo === 'ok' ? 'wf-toast wf-toast--success' : 'wf-review'} role={mensagem.tipo === 'erro' ? 'alert' : undefined}>
          {mensagem.tipo === 'erro' && <strong>Atenção</strong>}<p style={{ margin: 0 }}>{mensagem.texto}</p>
        </div>
      )}
      {!podeGerir && perfil && (
        <p className='wf-count'>Somente o perfil de TI e níveis superiores (N5 ou acima) podem tratar contas vencidas. Você pode consultar a lista.</p>
      )}
      {carregando ? <p className='wf-count'>Carregando...</p> : itens.length === 0 ? (
        <p className='wf-count'>Nenhuma conta vencida no momento.</p>
      ) : (
        <div className='wf-table-wrap'><table className='wf-table'>
          <thead><tr><th>Empresa</th><th>Etapa / Nível</th><th>Responsável</th><th>Valor</th><th>Atraso</th><th /></tr></thead>
          <tbody>{itens.map(i => (
            <React.Fragment key={i.id}>
              <tr>
                <td><strong>{i.empresa}</strong><span>{i.grupoNome}</span></td>
                <td><strong>{i.etapa}</strong><span>N{i.nivel}</span></td>
                <td>{i.responsavel}</td>
                <td>{money(i.valor)}</td>
                <td><span className='wf-danger-text'>{textoHoras(i.horasRestantes)}</span></td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  <button className='wf-link' onClick={() => history.push(`/workflow-aprovacao/conciliacoes/${i.conciliacaoId}`)}>Abrir</button>
                  {podeGerir && ACOES.map(a => (
                    <button key={a} className='wf-link' style={{ marginLeft: 10 }} onClick={() => abrir(i.id, a)}>{rotuloAcaoContaVencida[a]}</button>
                  ))}
                </td>
              </tr>
              {aberta?.id === i.id && (
                <tr><td colSpan={6}>
                  <strong style={{ fontSize: 13 }}>{rotuloAcaoContaVencida[aberta.acao]} — conciliação #{i.conciliacaoId}</strong>
                  {aberta.acao === 'PRORROGAR_PRAZO' && (
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginTop: 8 }}>
                      Novo prazo (SLA)
                      <input type='date' value={novoPrazo} onChange={e => { const v = e.target.value; setNovoPrazo(v); }} aria-label='Novo prazo' />
                    </label>
                  )}
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginTop: 8 }}>
                    Justificativa SOX (obrigatória)
                    <textarea rows={2} style={{ width: '100%' }} value={justificativa} onChange={e => { const v = e.target.value; setJustificativa(v); }}
                      placeholder='Motivo da ação na conta vencida...' />
                  </label>
                  <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
                    <button className='wf-btn wf-btn--primary wf-btn--sm' disabled={enviando} onClick={() => confirmar(i)}>
                      {enviando ? 'Enviando...' : `Confirmar: ${rotuloAcaoContaVencida[aberta.acao].toLowerCase()}`}
                    </button>
                    <button className='wf-btn wf-btn--secondary wf-btn--sm' onClick={() => setAberta(null)} disabled={enviando}>Cancelar</button>
                  </div>
                </td></tr>
              )}
            </React.Fragment>
          ))}</tbody>
        </table></div>
      )}
    </section>
  );
};

export default GovernancaContasVencidas;
